import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Search, Store, CheckCircle, XCircle, ShieldOff,
  ChevronDown, MapPin, Package, Percent, X, AlertTriangle, Plus, ExternalLink,
} from 'lucide-react'
import { vendorsService } from '@/services/vendorsService'
import { useDebounce } from '@/hooks/useDebounce'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import { formatDate, getLocalizedField, VENDOR_STATUS_LABELS } from '@/utils/formatters'

export default function VendorsPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  const STATUS_TABS = [
    { key: '',          label: t('vendors.tabAll'),        color: '#767683' },
    { key: 'pending',   label: t('vendors.pendingReview'), color: '#FF6D00' },
    { key: 'approved',  label: t('common.approved'),       color: '#1B6D24' },
    { key: 'rejected',  label: t('common.rejected'),       color: '#BA1A1A' },
    { key: 'suspended', label: t('common.suspended'),      color: '#454652' },
  ]
  const qc   = useQueryClient()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState('')
  const [search, setSearch]       = useState('')
  const { page, limit, setPage }  = usePagination(1, 15)
  const debouncedSearch = useDebounce(search, 400)

  // Dialogs
  const [rejectDialog,     setRejectDialog]     = useState({ open: false, vendor: null })
  const [commissionDialog, setCommissionDialog] = useState({ open: false, vendor: null })
  const [confirmDialog,    setConfirmDialog]    = useState({ open: false, vendor: null, action: null })
  const [rejectionReason,  setRejectionReason]  = useState('')
  const [commissionRate,   setCommissionRate]   = useState('')

  // Add Vendor dialog
  const [addVendorOpen, setAddVendorOpen] = useState(false)
  const [newVendor, setNewVendor] = useState({
    storeNameAr: '', storeNameEn: '', descriptionAr: '', descriptionEn: '',
    address: '', city: '', country: 'SA',
  })

  const createVendorMut = useMutation({
    mutationFn: () => vendorsService.create({
      storeName: { ar: newVendor.storeNameAr, en: newVendor.storeNameEn },
      description: { ar: newVendor.descriptionAr, en: newVendor.descriptionEn },
      address: newVendor.address,
      city: newVendor.city,
      country: newVendor.country,
    }),
    onSuccess: () => {
      toast.success(t('vendors.createSuccess'))
      qc.invalidateQueries({ queryKey: ['vendors'] })
      setAddVendorOpen(false)
      setNewVendor({ storeNameAr:'', storeNameEn:'', descriptionAr:'', descriptionEn:'', address:'', city:'', country:'SA' })
    },
    onError: () => toast.error(t('vendors.createError')),
  })

  // Query
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['vendors', { status: activeTab, search: debouncedSearch, page, limit }],
    queryFn: () => vendorsService.getAll({
      status: activeTab || undefined,
      search: debouncedSearch || undefined,
      page,
      limit,
    }),
  })

  const vendors = data?.data || []
  const meta    = data?.meta || {}

  // Pending count for badge
  const { data: pendingCountData } = useQuery({
    queryKey: ['vendors-pending-count'],
    queryFn: () => vendorsService.getAll({ status: 'pending', limit: 1 }),
    refetchInterval: 60_000,
  })
  const pendingCount = pendingCountData?.meta?.total || 0

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['vendors'] })
    qc.invalidateQueries({ queryKey: ['vendors-pending-count'] })
    qc.invalidateQueries({ queryKey: ['vendors-summary'] })
    qc.invalidateQueries({ queryKey: ['vendors-pending'] })
  }

  // Mutations
  const approveMut = useMutation({
    mutationFn: (id) => vendorsService.approve(id),
    onSuccess: () => { toast.success(t('vendors.approveSuccess')); invalidate(); setConfirmDialog({ open:false, vendor:null, action:null }) },
    onError: () => toast.error(t('common.failed')),
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }) => vendorsService.reject(id, reason),
    onSuccess: () => { toast.success(t('vendors.rejectSuccess')); invalidate(); setRejectDialog({ open:false, vendor:null }); setRejectionReason('') },
    onError: () => toast.error(t('common.failed')),
  })

  const suspendMut = useMutation({
    mutationFn: (id) => vendorsService.suspend(id),
    onSuccess: () => { toast.success(t('vendors.suspendSuccess')); invalidate(); setConfirmDialog({ open:false, vendor:null, action:null }) },
    onError: () => toast.error(t('common.failed')),
  })

  const commissionMut = useMutation({
    mutationFn: ({ id, rate }) => vendorsService.updateCommission(id, parseFloat(rate)),
    onSuccess: () => { toast.success(t('vendors.commissionSuccess')); invalidate(); setCommissionDialog({ open:false, vendor:null }); setCommissionRate('') },
    onError: () => toast.error(t('common.failed')),
  })

  // Handlers
  const handleTabChange = (key) => { setActiveTab(key); setPage(1) }
  const handleSearchChange = (v) => { setSearch(v); setPage(1) }

  const handleConfirm = () => {
    if (!confirmDialog.vendor) return
    if (confirmDialog.action === 'approve') approveMut.mutate(confirmDialog.vendor.id)
    if (confirmDialog.action === 'suspend') suspendMut.mutate(confirmDialog.vendor.id)
  }

  return (
    <div className="space-y-5">

      {/* Status Tabs */}
      <div className="hg-card p-1 flex gap-1 overflow-x-auto">
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key
          const showBadge = tab.key === 'pending' && pendingCount > 0
          return (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
                borderRadius: 12, fontSize: 13, fontWeight: 700,
                fontFamily: 'Cairo, sans-serif', border: 'none', cursor: 'pointer',
                whiteSpace: 'nowrap', transition: 'all 0.2s',
                background: isActive ? tab.color : 'transparent',
                color: isActive ? '#fff' : '#767683',
                boxShadow: isActive ? `0 4px 12px ${tab.color}30` : 'none',
              }}
            >
              {tab.label}
              {showBadge && (
                <span style={{ background:'#FF6D00', color:'#fff', fontSize:11, fontWeight:700, padding:'1px 7px', borderRadius:20, lineHeight:'18px' }}>
                  {pendingCount}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Search + summary + Add button */}
      <div className="hg-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-52">
          <Search size={16} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', color:'#767683', pointerEvents:'none' }} />
          <input
            className="hg-input"
            style={{ paddingRight: 40 }}
            placeholder={t('vendors.searchPlaceholder')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        {meta.total != null && (
          <span style={{ padding:'6px 12px', borderRadius:12, fontSize:13, fontWeight:700, background:'rgba(0,6,102,0.06)', color:'#000666', fontFamily:'Cairo, sans-serif' }}>
            {t('vendors.vendorCount', { count: meta.total?.toLocaleString(lang === 'ar' ? 'ar-EG' : 'en') })}
          </span>
        )}
        <button
          onClick={() => setAddVendorOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white"
          style={{ background: '#1B6D24', fontFamily: 'Cairo, sans-serif', flexShrink: 0 }}
        >
          <Plus size={16} />
          {t('vendors.addVendor')}
        </button>
      </div>

      {/* Vendor cards grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
      ) : isError ? (
        <div className="hg-card p-8 flex flex-col items-center gap-4">
          <p style={{ color:'#BA1A1A', fontWeight:700, fontFamily:'Cairo, sans-serif' }}>{t('common.loadFailed')}</p>
          <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">{t('common.retry')}</button>
        </div>
      ) : vendors.length === 0 ? (
        <div className="hg-card">
          <EmptyState icon={Store} message={t('vendors.emptyMessage')} />
        </div>
      ) : (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))', gap:16 }}>
            {vendors.map((vendor) => (
              <VendorCard
                key={vendor.id}
                vendor={vendor}
                lang={lang}
                t={t}
                onView={() => navigate(`/vendors/${vendor.id}`)}
                onApprove={() => setConfirmDialog({ open:true, vendor, action:'approve' })}
                onReject={()  => setRejectDialog({ open:true, vendor })}
                onSuspend={() => setConfirmDialog({ open:true, vendor, action:'suspend' })}
                onCommission={() => { setCommissionDialog({ open:true, vendor }); setCommissionRate(vendor.commissionRate ?? '') }}
              />
            ))}
          </div>
          <div className="hg-card p-4">
            <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage} />
          </div>
        </>
      )}

      {/* ── Approve / Suspend confirm dialog ────────────────────────────── */}
      {confirmDialog.open && (
        <DialogOverlay onClose={() => setConfirmDialog({ open:false, vendor:null, action:null })}>
          <div style={{ textAlign:'center', marginBottom:20 }}>
            <div style={{ width:52, height:52, borderRadius:16, background: confirmDialog.action==='approve' ? 'rgba(27,109,36,0.1)' : 'rgba(186,26,26,0.1)', color: confirmDialog.action==='approve' ? '#1B6D24' : '#BA1A1A', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
              {confirmDialog.action === 'approve' ? <CheckCircle size={26} /> : <ShieldOff size={26} />}
            </div>
            <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:18, color:'#1A1C1C', marginBottom:8 }}>
              {confirmDialog.action === 'approve' ? t('vendors.confirmApproveTitle') : t('vendors.confirmSuspendTitle')}
            </h3>
            <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:14, color:'#767683' }}>
              {confirmDialog.action === 'approve'
                ? t('vendors.confirmApproveBody', { name: getLocalizedField(confirmDialog.vendor?.storeName, lang) })
                : t('vendors.confirmSuspendBody', { name: getLocalizedField(confirmDialog.vendor?.storeName, lang) })}
            </p>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button
              onClick={handleConfirm}
              disabled={approveMut.isPending || suspendMut.isPending}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#fff', border:'none', cursor:'pointer', transition:'all 0.15s',
                background: confirmDialog.action === 'approve' ? '#1B6D24' : '#BA1A1A',
                opacity: (approveMut.isPending || suspendMut.isPending) ? 0.6 : 1 }}
            >
              {(approveMut.isPending || suspendMut.isPending) ? '...' : t('common.confirm')}
            </button>
            <button onClick={() => setConfirmDialog({ open:false, vendor:null, action:null })}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, background:'#F3F3F3', color:'#454652', border:'none', cursor:'pointer' }}>
              {t('common.cancel')}
            </button>
          </div>
        </DialogOverlay>
      )}

      {/* ── Reject dialog ─────────────────────────────────────────────── */}
      {rejectDialog.open && (
        <DialogOverlay onClose={() => { setRejectDialog({ open:false, vendor:null }); setRejectionReason('') }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
            <div style={{ width:44, height:44, borderRadius:14, background:'rgba(186,26,26,0.1)', color:'#BA1A1A', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <XCircle size={22} />
            </div>
            <div>
              <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:17, color:'#1A1C1C', margin:0 }}>{t('vendors.rejectDialogTitle')}</h3>
              <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683', margin:0 }}>{getLocalizedField(rejectDialog.vendor?.storeName, lang)}</p>
            </div>
          </div>
          <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, color:'#454652', marginBottom:8 }}>
            {t('vendors.rejectReasonLabel')}
          </label>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder={t('vendors.rejectReasonPlaceholder')}
            rows={4}
            style={{ width:'100%', borderRadius:12, background:'#E2E2E2', border:'none', padding:'12px 16px', fontSize:14, fontFamily:'Tajawal, sans-serif', resize:'vertical', outline:'none', boxSizing:'border-box', color:'#1A1C1C' }}
          />
          <div style={{ display:'flex', gap:10, marginTop:16 }}>
            <button
              onClick={() => rejectMut.mutate({ id: rejectDialog.vendor.id, reason: rejectionReason })}
              disabled={rejectMut.isPending || !rejectionReason.trim()}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#fff', background:'#BA1A1A', border:'none', cursor: !rejectionReason.trim() ? 'not-allowed' : 'pointer', opacity: (!rejectionReason.trim() || rejectMut.isPending) ? 0.5 : 1 }}
            >
              {rejectMut.isPending ? '...' : t('vendors.rejectSubmit')}
            </button>
            <button
              onClick={() => { setRejectDialog({ open:false, vendor:null }); setRejectionReason('') }}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, background:'#F3F3F3', color:'#454652', border:'none', cursor:'pointer' }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </DialogOverlay>
      )}

      {/* ── Commission dialog ─────────────────────────────────────────── */}
      {commissionDialog.open && (
        <DialogOverlay onClose={() => { setCommissionDialog({ open:false, vendor:null }); setCommissionRate('') }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
            <div style={{ width:44, height:44, borderRadius:14, background:'rgba(0,6,102,0.08)', color:'#000666', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <Percent size={22} />
            </div>
            <div>
              <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:17, color:'#1A1C1C', margin:0 }}>{t('vendors.commissionDialogTitle')}</h3>
              <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683', margin:0 }}>{getLocalizedField(commissionDialog.vendor?.storeName, lang)}</p>
            </div>
          </div>
          <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, color:'#454652', marginBottom:8 }}>
            {t('vendors.commissionRateLabel')}
          </label>
          <div style={{ position:'relative' }}>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              placeholder={t('vendors.commissionRatePlaceholder')}
              style={{ width:'100%', borderRadius:12, background:'#E2E2E2', border:'none', padding:'12px 16px', fontSize:16, fontFamily:'Cairo, sans-serif', fontWeight:700, outline:'none', boxSizing:'border-box', color:'#000666', textAlign:'center' }}
            />
            <span style={{ position:'absolute', left:16, top:'50%', transform:'translateY(-50%)', color:'#767683', fontWeight:700 }}>%</span>
          </div>
          <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:12, color:'#767683', marginTop:8 }}>
            {t('vendors.currentCommission', { rate: commissionDialog.vendor?.commissionRate ?? '—' })}
          </p>
          <div style={{ display:'flex', gap:10, marginTop:12 }}>
            <button
              onClick={() => commissionMut.mutate({ id: commissionDialog.vendor.id, rate: commissionRate })}
              disabled={commissionMut.isPending || !commissionRate}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#fff', background:'#000666', border:'none', cursor: !commissionRate ? 'not-allowed' : 'pointer', opacity: (!commissionRate || commissionMut.isPending) ? 0.5 : 1, boxShadow:'0 12px 32px rgba(26,35,126,0.15)' }}
            >
              {commissionMut.isPending ? '...' : t('common.save')}
            </button>
            <button
              onClick={() => { setCommissionDialog({ open:false, vendor:null }); setCommissionRate('') }}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, background:'#F3F3F3', color:'#454652', border:'none', cursor:'pointer' }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </DialogOverlay>
      )}

      {/* ── Add Vendor dialog ─────────────────────────────────────────────── */}
      {addVendorOpen && (
        <DialogOverlay onClose={() => setAddVendorOpen(false)}>
          <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:18, color:'#000666', marginBottom:4 }}>{t('vendors.addVendorTitle')}</h3>
          <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683', marginBottom:20 }}>
            {t('vendors.addVendorSubtitle')}
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>{t('vendors.storeNameArLabel')}</label>
              <input className="hg-input" placeholder={t('vendors.storeNameArPlaceholder')} value={newVendor.storeNameAr}
                onChange={e => setNewVendor(d => ({ ...d, storeNameAr: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif' }} />
            </div>
            <div>
              <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>{t('vendors.storeNameEnLabel')}</label>
              <input className="hg-input" placeholder="Tech World" value={newVendor.storeNameEn}
                onChange={e => setNewVendor(d => ({ ...d, storeNameEn: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif', direction:'ltr' }} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>{t('vendors.city')}</label>
              <input className="hg-input" placeholder={t('vendors.cityPlaceholder')} value={newVendor.city}
                onChange={e => setNewVendor(d => ({ ...d, city: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif' }} />
            </div>
            <div>
              <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>{t('vendors.countryLabel')}</label>
              <input className="hg-input" placeholder="SA" value={newVendor.country}
                onChange={e => setNewVendor(d => ({ ...d, country: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif', direction:'ltr' }} />
            </div>
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>{t('vendors.addressLabel')}</label>
            <input className="hg-input" placeholder={t('vendors.addressPlaceholder')} value={newVendor.address}
              onChange={e => setNewVendor(d => ({ ...d, address: e.target.value }))} style={{ fontFamily:'Tajawal, sans-serif' }} />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>{t('vendors.descriptionArLabel')}</label>
            <textarea rows={2} className="hg-input" style={{ resize:'vertical', fontFamily:'Tajawal, sans-serif' }} placeholder={t('vendors.descriptionPlaceholder')}
              value={newVendor.descriptionAr} onChange={e => setNewVendor(d => ({ ...d, descriptionAr: e.target.value }))} />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button
              onClick={() => createVendorMut.mutate()}
              disabled={!newVendor.storeNameAr.trim() || createVendorMut.isPending}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#fff', background:'#1B6D24', border:'none', cursor:'pointer', opacity: (!newVendor.storeNameAr.trim() || createVendorMut.isPending) ? 0.5 : 1 }}>
              {createVendorMut.isPending ? '...' : t('vendors.createVendorSubmit')}
            </button>
            <button onClick={() => setAddVendorOpen(false)}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, background:'#F3F3F3', color:'#454652', border:'none', cursor:'pointer' }}>
              {t('common.cancel')}
            </button>
          </div>
        </DialogOverlay>
      )}
    </div>
  )
}

// ── Vendor Card ────────────────────────────────────────────────────────────────
function VendorCard({ vendor, lang, t, onView, onApprove, onReject, onSuspend, onCommission }) {
  const statusMeta = VENDOR_STATUS_LABELS[vendor.status] || VENDOR_STATUS_LABELS.pending
  const storeName  = getLocalizedField(vendor.storeName, lang)
  const ownerName  = vendor.user?.name || '—'

  const actionsByStatus = {
    pending:   [
      { label:t('vendors.approve'), icon: CheckCircle, color:'#1B6D24', bg:'rgba(27,109,36,0.08)', onClick: onApprove },
      { label:t('vendors.reject'),  icon: XCircle,    color:'#BA1A1A', bg:'rgba(186,26,26,0.08)', onClick: onReject },
    ],
    approved:  [
      { label:t('vendors.suspend'),    icon: ShieldOff,  color:'#BA1A1A', bg:'rgba(186,26,26,0.08)', onClick: onSuspend },
      { label:t('vendors.commission'), icon: Percent,    color:'#000666', bg:'rgba(0,6,102,0.08)',   onClick: onCommission },
    ],
    rejected:  [
      { label:t('vendors.approve'), icon: CheckCircle, color:'#1B6D24', bg:'rgba(27,109,36,0.08)', onClick: onApprove },
    ],
    suspended: [
      { label:t('vendors.approve'), icon: CheckCircle, color:'#1B6D24', bg:'rgba(27,109,36,0.08)', onClick: onApprove },
    ],
  }

  const actions = actionsByStatus[vendor.status] || []

  return (
    <div style={{ background:'#fff', borderRadius:20, padding:20, boxShadow:'0 2px 4px rgba(26,28,28,0.04),0 8px 16px rgba(26,28,28,0.06)', transition:'transform 0.2s,box-shadow 0.2s' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(26,28,28,0.12)' }}
      onMouseLeave={(e) => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 2px 4px rgba(26,28,28,0.04),0 8px 16px rgba(26,28,28,0.06)' }}
    >
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:16 }}>
        {/* Logo placeholder */}
        <div style={{ width:48, height:48, borderRadius:14, background:'linear-gradient(135deg,#000666,#1A237E)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:20, fontWeight:900, fontFamily:'Cairo, sans-serif', flexShrink:0 }}>
          {storeName?.charAt(0) || 'V'}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:16, color:'#1A1C1C', marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{storeName}</div>
          <div style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683' }}>{ownerName}</div>
        </div>
        <span className={statusMeta.class}>{statusMeta[lang === 'ar' ? 'ar' : 'en']}</span>
      </div>

      {/* Info row */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:16 }}>
        {vendor.city && (
          <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#767683', fontFamily:'Tajawal, sans-serif' }}>
            <MapPin size={13} style={{ color:'#767683' }} />
            {vendor.city}{vendor.country ? `, ${vendor.country}` : ''}
          </div>
        )}
        {vendor._count?.products != null && (
          <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#767683', fontFamily:'Tajawal, sans-serif' }}>
            <Package size={13} style={{ color:'#767683' }} />
            {t('vendors.productsCount', { count: vendor._count.products })}
          </div>
        )}
        {vendor.commissionRate != null && (
          <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, fontWeight:700, fontFamily:'Cairo, sans-serif', color:'#000666' }}>
            <Percent size={13} />
            {vendor.commissionRate}%
          </div>
        )}
      </div>

      {/* Join date */}
      <div style={{ fontSize:11, color:'#767683', fontFamily:'Tajawal, sans-serif', marginBottom:16, paddingBottom:16, borderBottom:'1px solid #F3F3F3' }}>
        {t('vendors.joinedOn', { date: formatDate(vendor.createdAt, lang === 'ar' ? 'ar-EG' : 'en') })}
      </div>

      {/* Action buttons */}
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={onView}
          style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:5, padding:'8px 14px', borderRadius:10, fontSize:12, fontWeight:700, fontFamily:'Cairo, sans-serif', background:'rgba(0,6,102,0.07)', color:'#000666', border:'none', cursor:'pointer', transition:'all 0.15s', flexShrink:0 }}
          onMouseEnter={(e) => e.currentTarget.style.background='rgba(0,6,102,0.13)'}
          onMouseLeave={(e) => e.currentTarget.style.background='rgba(0,6,102,0.07)'}
        >
          <ExternalLink size={13} />{t('common.view')}
        </button>
        {actions.map(({ label, icon: Icon, color, bg, onClick }) => (
          <button key={label} onClick={onClick}
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'8px 0', borderRadius:10, fontSize:13, fontWeight:700, fontFamily:'Cairo, sans-serif', background:bg, color, border:'none', cursor:'pointer', transition:'all 0.15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.filter='brightness(0.92)'; e.currentTarget.style.transform='scale(1.03)' }}
            onMouseLeave={(e) => { e.currentTarget.style.filter=''; e.currentTarget.style.transform='' }}
          >
            <Icon size={14} />{label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Reusable dialog overlay ─────────────────────────────────────────────────
function DialogOverlay({ children, onClose }) {
  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="animate-in"
        style={{ background:'#fff', borderRadius:24, padding:28, width:'100%', maxWidth:440, boxShadow:'0 32px 64px rgba(0,0,0,0.2)', position:'relative' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} style={{ position:'absolute', top:16, left:16, width:32, height:32, borderRadius:10, background:'#F3F3F3', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#767683' }}>
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
  )
}
